<template>
  <ConfirmModal
    title="Edit Exposure Entry"
    size="md"
    :action="onConfirm"
    confirmText="Save"
    :dismiss="onDismiss"
    :show="show"
    @open="onOpen"
    @closed="$emit('closed')">
    <template v-slot:button="{ props: slotProps }">
      <AppButton
        v-if="!show && canEdit"
        class="tw:btn-square tw:btn-ghost"
        data-testid="edit-exposure-entry"
        v-bind="slotProps">
        <Edit2 :size="16" />
      </AppButton>
    </template>

    <form class="tw:px-2">
      <!-- effective date -->
      <div class="tw:mb-4">
        <DatePickerInput
          v-model="dirtyEffectiveDate"
          label="Effective Date"
          placeholder="Select date"
          class="tw:w-full" />
        <div
          v-if="effectiveDateErrors"
          class="tw:text-sm tw:text-error tw:mt-1">
          {{ effectiveDateErrors }}
        </div>
      </div>

      <!-- exposure values -->
      <div
        class="field-group"
        :class="{ noattr: !Array.isArray(exposureEntry.exposureAttributes) || exposureEntry.exposureAttributes.length === 0 }"
        :key="exposureEntry.exposureMetricID">
        <!-- value -->

        <NumberInput
          placeholder=" "
          :prefix="getPrefix(exposureEntry)"
          :suffix="getSuffix(exposureEntry)"
          :label="exposureEntry.name"
          v-model="dirtyValue"
          :error="valueErrors.length > 0"
          :error-message="valueErrors[0]"
          data-testid="exposure-value-input"/>

        <!-- attributes -->

        <template
          v-for="attr in [...entry.exposureAttributes].sort((a, b) => a.indexPos - b.indexPos)"
          :key="attr.exposureAttributeId">
          <div data-testid="edit-exposure-entry">
            <Combobox
              v-if="attr.lookupKey && attr.options && attr.options.length >= 100"
              :model-value="attr.value != null ? String(attr.value) : ''"
              @update:model-value="attr.value = $event"
              :options="(attr.options || []).map((o) => ({ label: o.text, value: String(o.value) }))"
              :label="attr.required ? attr.name : `${attr.name} (Optional)`"
              placeholder=" " />
            <div
              v-if="attr.lookupKey && attr.options && attr.options.length >= 100 && exposureAttributeErrors[attr.name]"
              class="tw:text-sm tw:text-error">
              {{ exposureAttributeErrors[attr.name] }}
            </div>

            <Combobox
              v-else-if="attr.lookupKey && attr.options && attr.options.length >= 4"
              :model-value="attr.value != null ? String(attr.value) : ''"
              @update:model-value="attr.value = $event"
              :options="(attr.options || []).map((o) => ({ label: o.text, value: String(o.value) }))"
              :label="attr.required ? attr.name : `${attr.name} (Optional)`"
              placeholder=" "
              class="tw:p-4" />
            <div
              v-if="attr.lookupKey && attr.options && attr.options.length >= 4 && exposureAttributeErrors[attr.name]"
              class="tw:text-sm tw:text-error">
              {{ exposureAttributeErrors[attr.name] }}
            </div>

            <div
              v-else-if="attr.options && attr.options.length > 0"
              class="tw:join">
              <input
                v-for="option in attr.options"
                type="radio"
                :key="option.value"
                name="attributeOption"
                :aria-label="option.text"
                class="tw:btn tw:join-item tw:btn-sm"
                @click="attr.value = option.value"
                :checked="attr.value === option.value" />
            </div>

            <template v-else-if="!attr.lookupKey">
              <TextField
                :model-value="attr.value"
                @update:model-value="attr.value = $event"
                :label="attr.name"
                placeholder=" "
                :error-message="exposureAttributeErrors[attr.name]"
                :error="!!exposureAttributeErrors[attr.name]" />
            </template>
          </div>
        </template>
      </div>
    </form>
  </ConfirmModal>
</template>

<script>
import AppButton from '@/components/v2/AppButton.vue';
import ConfirmModal from '@/components/ConfirmModal.vue';
import Combobox from '@/components/v2/Combobox.vue';
import DatePickerInput from '@/components/v2/DatePickerInput.vue';
import NumberInput from '@/components/v2/NumberInput.vue';
import TextField from '@/components/v2/TextField.vue';
import { Edit2 } from 'lucide-vue-next';
import { GetPrefixSymbolFromExposureMetric, GetSuffixSymbolFromExposureMetric } from '@/utilities/Exposures';
import * as Errors from '@/utilities/Errors';
import { DateTime } from 'luxon';

export default {
  components: { AppButton, ConfirmModal, Combobox, DatePickerInput, NumberInput, TextField, Edit2 },
  props: {
    exposureEntry: Object,
    update: Function,
    canEdit: Boolean,
    /** When true, modal opens on mount and trigger button is hidden (controlled mode). */
    show: { type: Boolean, default: false },
  },
  emits: ['closed'],
  data() {
    const effectiveDate = this.exposureEntry?.effectiveDate;
    const dirtyEffectiveDate = effectiveDate ? (DateTime.fromISO(effectiveDate, { zone: 'utc' }).toISODate() ?? '') : '';
    return {
      entry: JSON.parse(JSON.stringify(this.exposureEntry ?? {})),
      validationError: '',
      dirtyValue: this.exposureEntry?.value ?? '',
      dirtyEffectiveDate,
      exposureAttributeErrors: {},
    };
  },
  methods: {
    getPrefix: GetPrefixSymbolFromExposureMetric,
    getSuffix: GetSuffixSymbolFromExposureMetric,
    async onConfirm() {
      if (this.valueErrors.length > 0) {
        return false;
      }
      if (this.effectiveDateErrors.length > 0) {
        return false;
      }
      // validate attributes
      this.exposureAttributeErrors = this.entry.exposureAttributes.reduce((acc, a) => {
        const errors = a.required && !a.value ? Errors.FieldRequired : undefined;
        if (errors) {
          acc[a.name] = errors;
        }
        return acc;
      }, {});
      // cancel the update if any errors exist
      if (Object.keys(this.exposureAttributeErrors).length > 0) {
        return false;
      }
      return this.update({
        ...this.entry,
        value: this.dirtyValue,
        effectiveDate: DateTime.fromISO(this.dirtyEffectiveDate, { zone: 'utc' }).toISO(),
        exposureAttributeEntries: this.entry.exposureAttributes.map((a) => ({
          exposureAttributeID: a.exposureAttributeId,
          exposureAttributeValueID: a.exposureAttributeValueId,
          value: !a.value || ('' + a.value).trim().length === 0 ? null : a.value.trim(),
        })),
      });
    },
    onDismiss() {
      const entry = this.exposureEntry;
      if (!entry) return;
      this.dirtyValue = entry.value ?? '';
      const effectiveDate = entry.effectiveDate;
      this.dirtyEffectiveDate = effectiveDate ? (DateTime.fromISO(effectiveDate, { zone: 'utc' }).toISODate() ?? '') : '';
    },
    onOpen() {
      const entry = this.exposureEntry;
      if (!entry) return;
      this.entry = JSON.parse(JSON.stringify(entry));
      this.dirtyValue = entry.value ?? '';
      const effectiveDate = entry.effectiveDate;
      this.dirtyEffectiveDate = effectiveDate ? (DateTime.fromISO(effectiveDate, { zone: 'utc' }).toISODate() ?? '') : '';
    },
  },
  computed: {
    valueErrors() {
      if (this.dirtyValue === '' || isNaN(Number(this.dirtyValue))) {
        return ['Value is required'];
      }
      return [];
    },
    effectiveDateErrors() {
      return this.dirtyEffectiveDate === '' ? 'Effective Date is required' : '';
    },
  },
  watch: {
    exposureEntry: {
      handler(newVal) {
        if (!newVal) return;
        this.entry = JSON.parse(JSON.stringify(newVal));
        this.dirtyValue = newVal.value ?? '';
        const effectiveDate = newVal.effectiveDate;
        this.dirtyEffectiveDate = effectiveDate ? (DateTime.fromISO(effectiveDate, { zone: 'utc' }).toISODate() ?? '') : '';
      },
      immediate: true,
    },
  },
};
</script>

<style scoped>
form {
  padding-top: 1rem;
}

form .field-group {
  padding-bottom: 1rem;
  display: grid;
  grid-template-columns: 1fr 1fr;
  grid-column-gap: 2rem;
  grid-row-gap: 1rem;
  align-items: center;
  align-content: center;
}

form .field-group.noattr {
  grid-template-columns: 1fr;
}
</style>
